/** Shared Stripe Connect server functions for coach onboarding. */
import { createServerFn } from "@tanstack/react-start";
import { getRequestUrl } from "@tanstack/react-start/server";
import type Stripe from "stripe";
import { z } from "zod";

import { requireClerkCoachAuth } from "~/lib/coach-middleware";

async function fetchStripeAccount(accountId: string): Promise<Stripe.Account> {
  const { getStripe } = await import("~/lib/stripe");
  const stripe = await getStripe();
  return stripe.accounts.retrieve(accountId);
}

export const getRefreshedCoachFn = createServerFn({ method: "GET" })
  .middleware([requireClerkCoachAuth])
  .validator(
    z.object({
      accountId: z.string().optional(),
    }),
  )
  .handler(async ({ data, context }) => {
    const { updateCoach } = await import("~/lib/server/coach");
    const coach = context.coach;

    if (data.accountId) {
      if (coach.stripeAccountId !== data.accountId) {
        return { coach, synced: false };
      }

      const account = await fetchStripeAccount(data.accountId);

      return {
        account,
        synced: true,
        coach: await updateCoach(coach.id, {
          stripeAccountId: account.id,
          stripeChargesEnabled: account.charges_enabled,
          stripePayoutsEnabled: account.payouts_enabled,
        }),
      };
    }

    // Always sync Stripe state to DB when we have an account
    if (coach.stripeAccountId) {
      const account = await fetchStripeAccount(coach.stripeAccountId);
      const needsSync =
        coach.stripeChargesEnabled !== account.charges_enabled ||
        coach.stripePayoutsEnabled !== account.payouts_enabled;

      const updatedCoach = needsSync
        ? await updateCoach(coach.id, {
            stripeChargesEnabled: account.charges_enabled,
            stripePayoutsEnabled: account.payouts_enabled,
          })
        : coach;

      return { coach: updatedCoach, account, synced: needsSync };
    }

    return { coach, account: undefined, synced: false };
  });

export const connectCoachToStripeFn = createServerFn({ method: "POST" })
  .middleware([requireClerkCoachAuth])
  .handler(async ({ context }) => {
    const { createCoachOnboardingLink } =
      await import("~/lib/server/stripe-connect");
    const requestUrl = getRequestUrl();
    const origin = `${requestUrl.protocol}//${requestUrl.host}`;
    return { url: await createCoachOnboardingLink(context.coach, origin) };
  });
